import { type CSSProperties, Suspense, useEffect, useRef } from 'react';
import { Outlet, useNavigate, Navigate } from 'react-router-dom';
import { CONVERSION_POPUP_TRIGGER_SOURCES } from 'services/mixpanel/consts';
import { NavigationGuardProvider } from 'shared/NavigationGuard';
import { ConversionPopup } from '../../components/Billing/ConversionPopup';
import { MigrationWelcomePopup } from '../../components/Billing/MigrationWelcomePopup';
import Header from '../../components/Header';
import { Loader } from '../../components/Loader';
import { MaintenanceAlert } from '../../components/MaintenanceAlert';
import Sidebar from '../../components/Sidebar';
import { useBillingBanner } from '../../hooks/billing/useBillingBanner';
import {
  useConversionPopup,
  ConversionPopupProvider,
} from '../../hooks/billing/useConversionPopup';
import { useReversedPendingCheckout } from '../../hooks/billing/useReversedPendingCheckout';
import { useAwareFlow } from '../../hooks/useAwareFlow';
import { useMaintenanceMode } from '../../hooks/useMaintenanceMode';
import { usePathSegments } from '../../hooks/usePathSegments';
import { Error } from '../../pages/Error/Error';
import { ROUTES } from '../../routes/consts';
import { cn } from '../../utils/className';
import { isBillingPath } from '../../utils/routes';

/**
 * Fires useConversionPopup().open() once when a blocked billing state is
 * detected (autoOpenPopup=true per D15). Remounts on navigation so blocked
 * state always auto-opens on fresh page visits.
 */
const AutoOpenGate = () => {
  const vm = useBillingBanner();
  const { open } = useConversionPopup();
  const firedRef = useRef(false);

  // A reversed Move that hasn't bought yet (self-serve, no subscription) keeps
  // getting nudged into checkout — the migration UI is gone (they're self-serve),
  // but they still need to pick a plan (with their coupon). Stops once they buy.
  const reversedNeedsCheckout = useReversedPendingCheckout();
  const shouldAutoOpen = vm?.autoOpenPopup || reversedNeedsCheckout;

  useEffect(() => {
    if (shouldAutoOpen && !firedRef.current) {
      firedRef.current = true;
      open(undefined, CONVERSION_POPUP_TRIGGER_SOURCES.TRIAL_END_BLOCK);
    }
  }, [shouldAutoOpen, open]);

  return null;
};

const MainContent = () => {
  const navigate = useNavigate();
  const pathSegments = usePathSegments();
  const {
    isAwareMode,
    isPending,
    isSuccess,
    isError,
    apiKey,
    awareData,
    handleAwareFlow,
  } = useAwareFlow();

  const { isMaintenanceMode } = useMaintenanceMode();

  const isBillingPage = isBillingPath();
  const layoutStyle = (
    isBillingPage ? { '--main-header-height': '112px' } : undefined
  ) as CSSProperties | undefined;

  useEffect(() => {
    const isRootPath = pathSegments.length === 0;

    if (isRootPath && !isAwareMode) {
      navigate(ROUTES.HOME);
    }
  }, [navigate, pathSegments, isAwareMode]);

  useEffect(() => {
    if (isAwareMode) {
      handleAwareFlow();
    }
  }, [isAwareMode, handleAwareFlow]);

  if (isAwareMode && (isPending || (!isSuccess && !isError))) {
    return <Loader center />;
  }

  if (isSuccess) {
    return (
      <Navigate
        to={ROUTES.AWARE_SUCCESS}
        replace
        state={{ apiKey, returnURL: awareData.returnURL }}
      />
    );
  }

  if (isError) {
    return <Error type="FALLBACK" />;
  }

  const layoutClassName = cn(
    'h-screen grid grid-cols-[fit-content(var(--sidebar-width))_1fr] bg-app-background overflow-hidden grid-areas-layout grid-rows-[var(--main-header-height)_1fr]',
  );

  return (
    <div className={layoutClassName} style={layoutStyle}>
      <Sidebar />
      <Header />
      <main className="grid-in-main relative overflow-y-auto">
        {isMaintenanceMode && <MaintenanceAlert />}
        <Suspense fallback={<Loader center />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
};

export const Main = () => (
  <ConversionPopupProvider>
    <AutoOpenGate />
    <ConversionPopup />
    <MigrationWelcomePopup />
    <NavigationGuardProvider>
      <MainContent />
    </NavigationGuardProvider>
  </ConversionPopupProvider>
);
